/**
 * User-facing error fallback. Maps LoaderError → DOM message inside #quiz-root.
 *
 * Discipline: textContent only, never innerHTML. Each case also logs to
 * console.error so devs hitting the embedded iframe in DevTools see the
 * full kind + debug detail. User copy is intentionally generic for
 * `not_found` (does NOT distinguish "doesn't exist" from "wrong publisher").
 */

import type { LoaderError } from '@/runtime/loader';

function userMessage(error: LoaderError): string {
  switch (error.kind) {
    case 'missing_params':
      return "This quiz can't be loaded — the embed link is missing required information.";
    case 'not_found':
      return 'Quiz not available.';
    case 'network_error':
      return "Couldn't load the quiz right now. Please refresh in a moment.";
    case 'parse_error':
    case 'validation_error':
      return "This quiz is currently misconfigured and can't be displayed.";
    case 'schema_version_mismatch':
      return 'This quiz needs a newer version of the runtime. The page will update automatically soon.';
  }
}

function debugDetail(error: LoaderError): string {
  switch (error.kind) {
    case 'missing_params':
      return error.details;
    case 'not_found':
      return '404';
    case 'network_error':
      return `status=${error.status ?? 'null'} ${error.details}`;
    case 'parse_error':
      return error.details;
    case 'schema_version_mismatch':
      return `received=${error.received} supported=${error.supported}`;
    case 'validation_error':
      return `${error.path}: ${error.details}`;
  }
}

export function renderError(container: HTMLElement, error: LoaderError): void {
  console.error(`[quiz-runtime] ${error.kind}:`, debugDetail(error));

  // Clear any prior content (e.g. preview placeholder) before swapping in
  // the alert. Avoids stacking when called after a partial mount.
  container.replaceChildren();

  const alertEl = document.createElement('div');
  alertEl.setAttribute('role', 'alert');
  alertEl.style.maxWidth = '32rem';
  alertEl.style.padding = '1.25rem 1.5rem';
  alertEl.style.borderRadius = '0.5rem';
  alertEl.style.background = 'hsl(0, 0%, 97%)';
  alertEl.style.border = '1px solid hsl(0, 0%, 90%)';
  alertEl.style.color = 'var(--quiz-text)';
  alertEl.style.fontSize = '0.95rem';
  alertEl.style.lineHeight = '1.5';
  alertEl.style.textAlign = 'center';
  alertEl.textContent = userMessage(error);

  container.appendChild(alertEl);
}
